import React, { useEffect, useState } from 'react';
import {
  Alert,
  Divider,
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Button,
  Space,
  Typography,
  message,
} from 'antd';
import { SafetyCertificateOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  TIPO_CERTIFICACION_OPTIONS,
  ESTADO_CERTIFICACION_OPTIONS,
} from 'providers/Certificaciones';
import CertificacionAttachmentsSection from './CertificacionAttachmentsSection';

const { Title } = Typography;
const { TextArea } = Input;

// Drawer para crear/editar una certificación MINVU de un equipo.
// En modo edición muestra además la sección de Adjuntos (PDF del certificado
// MINVU + acta + fotos + anexos) via CertificacionAttachmentsSection. En modo
// creación los adjuntos se deshabilitan hasta guardar — el id es necesario
// para generar la S3 key.
function CertificacionEditorDrawer({ open, certificacion, onClose, onSubmit }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = Boolean(certificacion?.id);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (certificacion) {
      form.setFieldsValue({
        tipo: certificacion.tipo ?? 'residencial',
        fecha_emision: certificacion.fecha_emision ? dayjs(certificacion.fecha_emision) : null,
        fecha_vencimiento: certificacion.fecha_vencimiento
          ? dayjs(certificacion.fecha_vencimiento)
          : null,
        entidad_certificadora: certificacion.entidad_certificadora ?? '',
        num_certificado: certificacion.num_certificado ?? '',
        sello_verde: Boolean(certificacion.sello_verde),
        observaciones_menores: certificacion.observaciones_menores ?? '',
        observaciones_mayores: certificacion.observaciones_mayores ?? '',
        estado: certificacion.estado ?? undefined,
      });
    } else {
      form.setFieldsValue({
        tipo: 'residencial',
        sello_verde: false,
      });
    }
  }, [open, certificacion, form]);

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        tipo: values.tipo,
        fecha_emision: dayjs(values.fecha_emision).format('YYYY-MM-DD'),
        fecha_vencimiento: dayjs(values.fecha_vencimiento).format('YYYY-MM-DD'),
        entidad_certificadora: values.entidad_certificadora?.trim(),
        num_certificado: values.num_certificado?.trim() || null,
        sello_verde: Boolean(values.sello_verde),
        observaciones_menores: values.observaciones_menores?.trim() || null,
        observaciones_mayores: values.observaciones_mayores?.trim() || null,
        estado: values.estado || undefined,
      };
      await onSubmit(payload);
      message.success(isEdit ? 'Certificación actualizada' : 'Certificación registrada');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'No se pudo guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={
        <Space align="center">
          <SafetyCertificateOutlined />
          <Title level={5} style={{ margin: 0 }}>
            {isEdit ? `Editar certificación #${certificacion.id}` : 'Registrar certificación'}
          </Title>
        </Space>
      }
      placement="right"
      width={560}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="primary"
            icon={isEdit ? <SaveOutlined /> : <PlusOutlined />}
            loading={submitting}
            onClick={handleFinish}
          >
            {isEdit ? 'Guardar' : 'Registrar'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label="Tipo"
          name="tipo"
          rules={[{ required: true, message: 'Selecciona el tipo' }]}
        >
          <Select options={TIPO_CERTIFICACION_OPTIONS} />
        </Form.Item>

        <Space.Compact block>
          <Form.Item
            label="Fecha de emisión"
            name="fecha_emision"
            style={{ flex: 1, marginRight: 8 }}
            rules={[{ required: true, message: 'Obligatoria' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            label="Fecha de vencimiento"
            name="fecha_vencimiento"
            style={{ flex: 1 }}
            dependencies={['fecha_emision']}
            rules={[
              { required: true, message: 'Obligatoria' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const emision = getFieldValue('fecha_emision');
                  if (!value || !emision) return Promise.resolve();
                  return dayjs(value).isAfter(dayjs(emision))
                    ? Promise.resolve()
                    : Promise.reject(new Error('Debe ser posterior a la emisión'));
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          label="Entidad certificadora"
          name="entidad_certificadora"
          rules={[
            { required: true, message: 'Obligatoria' },
            { max: 180, message: 'Máximo 180 caracteres' },
          ]}
        >
          <Input placeholder="Ej: DICTUC, IDIEM, Applus+" />
        </Form.Item>

        <Form.Item label="N° certificado" name="num_certificado">
          <Input placeholder="CERT-2024-001234" />
        </Form.Item>

        <Form.Item
          label="Sello verde"
          name="sello_verde"
          valuePropName="checked"
          tooltip="Sello MINVU sin observaciones mayores"
        >
          <Switch />
        </Form.Item>

        {isEdit && (
          <Form.Item
            label="Estado (manual)"
            name="estado"
            tooltip="Normalmente lo computa el backend por fecha; override manual sólo si hay una razón explícita"
          >
            <Select allowClear options={ESTADO_CERTIFICACION_OPTIONS} />
          </Form.Item>
        )}

        <Form.Item label="Observaciones menores" name="observaciones_menores">
          <TextArea rows={3} placeholder="Detalles corregidos en sitio" />
        </Form.Item>

        <Form.Item label="Observaciones mayores" name="observaciones_mayores">
          <TextArea rows={3} placeholder="Observaciones que requieren reinspección" />
        </Form.Item>
      </Form>

      <Divider style={{ margin: '16px 0' }} />

      {isEdit ? (
        <CertificacionAttachmentsSection certificacionId={certificacion.id} />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Los adjuntos se habilitan al guardar la certificación"
          description="Primero registra los datos de la certificación; luego vas a poder subir el PDF del certificado MINVU y cualquier anexo."
        />
      )}
    </Drawer>
  );
}

export default CertificacionEditorDrawer;
